import { createStackNavigator, StackHeaderProps } from '@react-navigation/stack'
import type { ReactNode } from 'react'
import { View } from 'react-native'
import {
  MelddNavigationBar,
  MelddNavigationBarProps,
} from '../@ui/headers/meldd.header'
import {
  NodeNavigationBar,
  NodeNavigationBarProps,
} from '../screens/core/clusters/node-navigation.header'
import { AcceptInvitationScreen } from '../screens/core/clusters/accept-invitation.screen'
import { ClusterCardsScreen } from '../screens/core/clusters/cluster-cards/cluster-card.screen'
import { ClusterListScreen } from '../screens/core/clusters/cluster-list/cluster-list.screen'
import { LoginMobileNumberScreen } from '../screens/auth/login/login.screen'
import {
  PerspectiveNavigationBar,
  PerspectiveNavigationBarProps,
} from '../screens/core/perspectives/perspective/header/perspective.header'
import PerspectiveScreen from '../screens/core/perspectives/perspective/perspective.screen'
import { PrivacyPolicyScreen } from '../screens/auth/privacy-policy.screen'
import {
  ProfileNavigationBar,
  ProfileNavigationBarProps,
} from '../screens/settings/profile/components/profile.header'
import { RegisterScreen } from '../screens/auth/register/register.screen'
import WechatRedirect from '../screens/auth/redirects/wechat-redirect.page'
import { VisualizationNavigationBar } from '../screens/visualization/visualization.header'
import { AppNavigatorParams } from './types'
import i18n from '../i18n'

/**
 * Lazy loads can not be access directly via URL on the web version.
 * For that do a normal import.
 */
import {
  LoginNavigationBar,
  RegisterNavigationBar,
} from '../screens/auth/login/login.header'

import UserContributionsScreen from '../screens/core/contributions/user-contributions.screen'
import CreateArticleScreen from '../screens/core/perspectives/create-article/create-article.screen'
import { CreateArticleHeader } from '../screens/core/perspectives/create-article/create-article.header'
import FeatureTogglesScreen from '../screens/settings/settings.screen'
import IntroScreen from '../screens/settings/tutorial.screen'
import NodeStatsScreen from '../screens/core/clusters/node-leaderboard/node-leaderboard.screen'
import NodeStatsAdminScreen from '../screens/node-admin/node-stats/node-stats-admin.screen'
import ProfileScreen from '../screens/settings/profile/profile.screen'
import ProfileVerifyMobileScreen from '../screens/settings/profile/verify-mobile.screen'
import MyTasksScreen from '../screens/tasks/my-tasks.screen'
import TaskDetail from '../screens/tasks/task-detail.screen'
import { TaskListScreen } from '../screens/tasks/task-list.screen'
import VisualizationScreen from '../screens/visualization/visualization.screen'
import { TutorialProvider } from '../@ui/containers/others/tutorial/tutorial.context'
import { useAuthContext } from '../contexts/auth-context'
import { ReplayNavigator } from './replay.navigator'
import { AdminNavigator } from './admin.navigator'
import NodeSettingsScreen from '../screens/node-admin/node-settings.screen'
import NodeMembersScreen from '../screens/node-admin/node-members/node-members.screen'
import InvitationLinksScreen from '../screens/node-admin/invitation-links/invitation-links.screen'
import ListNodesScreen from '../screens/core/clusters/nodes-list/node-list.screen'
import ClusterContributionsScreen from '../screens/core/contributions/cluster-contribution.screen'
import DecisionsScreen from '../screens/core/contributions/contribution-decision.screen'
import CreateClusterScreen from '../screens/core/clusters/cluster/create-cluster.screen'
import ClusterSettingsScreen from '../screens/core/clusters/cluster/cluster-settings.screen'
import SearchScreen from '../screens/core/clusters/search/search.screen'
import PerspectiveContributionsScreen from '../screens/core/contributions/perspective-contributions.screen'
import PerspectiveContributionScreen from '../screens/core/contributions/contribution.screen'
import { ReplayScreen } from '../screens/core/replay/replay.screen'
import { MyNodesScreen } from '../screens/node-admin/my-nodes/my-nodes.screen'
import { BillingListScreen } from '../screens/node-admin/billing/billing-list.screen'
import { PaymentMethodsScreen } from '../screens/settings/payment-methods/payment-methods.screen'
import { MyCollaboratorsScreen } from '../screens/settings/collaborators/my-collaborators.screen'
import { PricingScreen } from '../screens/pricing/pricing.screen'
import { OfflineModeBanner } from '../@ui/containers/offline-mode.banner'
import { withRequiredAuth } from './with-required-auth'

const Stack = createStackNavigator<AppNavigatorParams>()

const screenTitle = (name: keyof AppNavigatorParams) => ({
  title: i18n.t(`screens:${name}`),
})

const renderHeaderWithOfflineBanner = (header: ReactNode) => (
  <View>
    {header}
    <OfflineModeBanner />
  </View>
)

// We require export the names for web3, on reload a page they need to match URI with screen
export const AppNavigatorScreens = {
  WechatRedirect: 'WechatRedirect',
  Visualization: 'Visualization',
  ListNodes: 'ListNodes',
  MyNodes: 'MyNodes',
  Admin: 'admin',
  BillingList: 'BillingList',
  PaymentMethods: 'PaymentMethods',
  ClusterList: 'ClusterList',
  ClusterCards: 'ClusterCards',
  ClusterContributions: 'ClusterContributions',
  Perspective: 'Perspective',
  PerspectiveCards: 'PerspectiveCards',
  PerspectiveContributions: 'PerspectiveContributions',
  PerspectiveContribution: 'PerspectiveContribution',
  CreateArticle: 'CreateArticle',
  CreateCluster: 'CreateCluster',
  CreateNode: 'CreateNode',
  ClusterSettings: 'ClusterSettings',
  NodeSettings: 'NodeSettings',
  NodeStats: 'NodeStats',
  NodeStatsAdmin: 'NodeStatsAdmin',
  ProfileVerifyMobile: 'ProfileVerifyMobile',
  Profile: 'Profile',
  Decisions: 'Decisions',
  Settings: 'Settings',
  Intro: 'Intro',
  UserContributions: 'UserContributions',
  LoginMobileNumber: 'LoginMobileNumber',
  Register: 'Register',
  PrivacyPolicy: 'PrivacyPolicy',
  MyTasks: 'MyTasks',
  Task: 'task/:taskId?',
  TaskList: 'task-list/:targetType/:targetId',
  Search: 'Search',
  NodeMembers: 'NodeMembers',
  InvitationLinks: 'InvitationLinks',
  AcceptInvitation: 'invitation/:code',
  ReplayList: 'replay-list/:targetType/:targetId',
  MyCollaborators: 'MyCollaborators',
  Pricing: 'Pricing',
}

const AppNavigator = () => {
  return (
    <Stack.Navigator
      screenOptions={{
        header: (props: StackHeaderProps) =>
          renderHeaderWithOfflineBanner(
            <MelddNavigationBar {...(props as MelddNavigationBarProps)} />
          ),
      }}
    >
      <Stack.Screen
        name="WechatRedirect"
        component={WechatRedirect}
        options={{
          ...screenTitle('WechatRedirect'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <NodeNavigationBar {...(props as NodeNavigationBarProps)} />
            ),
        }}
      />
      <Stack.Screen
        name="Visualization"
        component={VisualizationScreen}
        options={{
          ...screenTitle('Visualization'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <VisualizationNavigationBar
                {...(props as NodeNavigationBarProps)}
              />
            ),
        }}
      />
      <Stack.Screen
        name="ListNodes"
        component={ListNodesScreen}
        options={{
          ...screenTitle('ListNodes'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
      <Stack.Screen
        name="MyNodes"
        component={withRequiredAuth(MyNodesScreen)}
        options={{
          ...screenTitle('MyNodes'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
      <Stack.Screen
        name="Admin"
        component={AdminNavigator}
        options={{ ...screenTitle('Admin'), headerShown: false }}
      />
      <Stack.Screen
        name="BillingList"
        component={withRequiredAuth(BillingListScreen)}
        options={{
          ...screenTitle('BillingList'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
      <Stack.Screen
        name="PaymentMethods"
        component={withRequiredAuth(PaymentMethodsScreen)}
        options={{
          ...screenTitle('PaymentMethods'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
      <Stack.Screen
        name="ClusterList"
        component={(props: any) => (
          <TutorialProvider>
            <ClusterListScreen {...props} />
          </TutorialProvider>
        )}
        options={{
          ...screenTitle('ClusterList'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <NodeNavigationBar {...(props as NodeNavigationBarProps)} />
            ),
        }}
      />
      <Stack.Screen
        name="ClusterCards"
        component={(props: any) => (
          <TutorialProvider>
            <ClusterCardsScreen {...props} />
          </TutorialProvider>
        )}
        options={{
          ...screenTitle('ClusterCards'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <NodeNavigationBar {...(props as NodeNavigationBarProps)} />
            ),
        }}
      />
      <Stack.Screen
        name="ClusterContributions"
        component={ClusterContributionsScreen}
        options={screenTitle('ClusterContributions')}
      />
      <Stack.Screen
        name="Perspective"
        component={(props: any) => (
          <TutorialProvider>
            <PerspectiveScreen {...props} />
          </TutorialProvider>
        )}
        options={{
          ...screenTitle('Perspective'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <PerspectiveNavigationBar
                {...(props as PerspectiveNavigationBarProps)}
              />
            ),
        }}
      />

      <Stack.Screen
        name="PerspectiveContributions"
        component={PerspectiveContributionsScreen}
        options={screenTitle('PerspectiveContributions')}
      />
      <Stack.Screen
        name="PerspectiveContribution"
        component={PerspectiveContributionScreen}
        options={screenTitle('PerspectiveContribution')}
      />
      <Stack.Screen
        name="CreateArticle"
        component={CreateArticleScreen}
        options={screenTitle('CreateArticle')}
      />
      <Stack.Screen
        name="CreateCluster"
        component={CreateClusterScreen}
        options={screenTitle('CreateCluster')}
      />
      <Stack.Screen
        name="ClusterSettings"
        component={ClusterSettingsScreen}
        options={screenTitle('ClusterSettings')}
      />
      <Stack.Screen
        name="NodeSettings"
        component={withRequiredAuth(NodeSettingsScreen)}
        options={screenTitle('NodeSettings')}
      />
      <Stack.Screen
        name="NodeStats"
        component={NodeStatsScreen}
        options={screenTitle('NodeStats')}
      />
      <Stack.Screen
        name="NodeStatsAdmin"
        component={NodeStatsAdminScreen}
        options={screenTitle('NodeStatsAdmin')}
      />
      <Stack.Screen
        name="NodeMembers"
        component={NodeMembersScreen}
        options={screenTitle('NodeMembers')}
      />
      <Stack.Screen
        name="InvitationLinks"
        component={InvitationLinksScreen}
        options={screenTitle('InvitationLinks')}
      />
      <Stack.Screen
        name="ProfileVerifyMobile"
        component={ProfileVerifyMobileScreen}
        options={screenTitle('ProfileVerifyMobile')}
      />
      <Stack.Screen
        name="Profile"
        component={withRequiredAuth(ProfileScreen)}
        options={{
          ...screenTitle('Profile'),
          header: (props: StackHeaderProps) => {
            const params = (props as ProfileNavigationBarProps).route.params
            const userId = params
              ? (params as { userId: string }).userId
              : undefined
            const { userId: loggedInUserId } = useAuthContext()
            if (!userId || userId !== loggedInUserId) {
              return renderHeaderWithOfflineBanner(
                <MelddNavigationBar {...(props as MelddNavigationBarProps)} />
              )
            }
            return renderHeaderWithOfflineBanner(
              <ProfileNavigationBar {...(props as ProfileNavigationBarProps)} />
            )
          },
        }}
      />
      <Stack.Screen
        name="Decisions"
        component={DecisionsScreen}
        options={screenTitle('Decisions')}
      />
      <Stack.Screen
        name="Settings"
        component={FeatureTogglesScreen}
        options={screenTitle('Settings')}
      />
      <Stack.Screen
        name="Intro"
        component={IntroScreen}
        options={screenTitle('Intro')}
      />
      <Stack.Screen
        name="UserContributions"
        component={UserContributionsScreen}
        options={screenTitle('UserContributions')}
      />
      <Stack.Screen
        name="AcceptInvitation"
        component={AcceptInvitationScreen}
        options={screenTitle('AcceptInvitation')}
      />
      <Stack.Screen
        name="LoginMobileNumber"
        component={LoginMobileNumberScreen}
        options={{
          ...screenTitle('LoginMobileNumber'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <LoginNavigationBar {...(props as MelddNavigationBarProps)} />
            ),
        }}
      />
      <Stack.Screen
        name="Register"
        component={RegisterScreen}
        options={{
          ...screenTitle('Register'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <RegisterNavigationBar {...(props as MelddNavigationBarProps)} />
            ),
        }}
      />
      <Stack.Screen
        name="PrivacyPolicy"
        component={PrivacyPolicyScreen}
        options={screenTitle('PrivacyPolicy')}
      />
      <Stack.Screen
        name="Task"
        component={TaskDetail}
        options={screenTitle('Task')}
      />
      <Stack.Screen
        name="MyTasks"
        component={withRequiredAuth(MyTasksScreen)}
        options={screenTitle('MyTasks')}
      />
      <Stack.Screen
        name="TaskList"
        component={TaskListScreen}
        options={screenTitle('TaskList')}
      />
      <Stack.Screen
        name="Search"
        component={SearchScreen}
        options={screenTitle('Search')}
      />
      <Stack.Screen
        name="ReplayList"
        component={ReplayScreen}
        options={screenTitle('ReplayList')}
      />

      <Stack.Screen
        name="MyCollaborators"
        component={withRequiredAuth(MyCollaboratorsScreen)}
        options={{
          ...screenTitle('MyCollaborators'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />

      <Stack.Screen
        name="Replay"
        component={ReplayNavigator}
        options={{ ...screenTitle('Replay'), headerShown: false }}
      />

      <Stack.Screen
        name="Pricing"
        component={PricingScreen}
        options={{
          ...screenTitle('Pricing'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
    </Stack.Navigator>
  )
}

export default AppNavigator
